import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from "framer-motion";
import FloatingActionButtons from "./FloatingActionButtons";
import { Button } from "@/components/ui/button";

// Using public image for better performance
const heroImage = "/house-cleaners.webp";

const QuoteForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<{
    name: string;
    service: string;
    phone?: string;
    email: string;
    description: string;
    consent: boolean;
  }>({
    defaultValues: { consent: true },
  });

  const onSubmit = async (data: {
    name: string;
    service: string;
    phone?: string;
    email: string;
    description: string;
    consent: boolean;
  }) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Trim and normalize input
      const payload = {
        name: data.name.trim(),
        service: data.service.trim(),
        phone: data.phone?.trim() || "",
        email: data.email.trim().toLowerCase(),
        description: data.description?.trim().slice(0, 2000) || "",
        consent: data.consent,
      };
      
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let result: any = {};
      try {
        result = await response.json();
      } catch {
        result = { message: "Serverfel" };
      }

      if (!response.ok) {
        throw new Error(result.message || 'Något gick fel. Försök igen senare.');
      }

      setSubmitSuccess(true);
      reset();
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error instanceof Error ? error.message : error);
      setSubmitError(error instanceof Error ? error.message : 'Något gick fel. Försök igen senare.');
      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-2 sm:space-y-3 lg:space-y-3 [@media(min-width:1280px)_and_(max-height:650px)]:space-y-2.5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-1">
            Namn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            disabled={isSubmitting}
            aria-invalid={!!errors.name}
            {...register('name', { required: 'Namn är obligatoriskt', maxLength: 100 })}
            className={`w-full px-3 py-2 text-sm border ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-500 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            placeholder="Ditt namn"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
          )}
        </div>
        
        {/* Service */}
        <div>
          <label htmlFor="service" className="block text-sm font-semibold text-gray-800 mb-1">
            Tjänst <span className="text-red-500">*</span>
          </label>
          <select
            id="service"
            disabled={isSubmitting}
            aria-invalid={!!errors.service}
            {...register('service', { required: 'Vänligen välj en tjänst' })}
            className={`w-full px-3 py-2 text-sm border ${
              errors.service ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-white font-semibold text-gray-800 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <option value="">Välj tjänst</option>
            <option value="hemstädning">Hemstädning</option>
            <option value="kontorsstädning">Kontorsstädning</option>
            <option value="flyttstädning">Flyttstädning</option>
            <option value="storstädning">Storstädning</option>
            <option value="fönsterputs">Fönsterputs</option>
            <option value="trappstädning">Trappstädning</option>
          </select>
          {errors.service && (
            <p className="mt-1 text-xs text-red-600">{errors.service.message}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-1">
            Telefon
          </label>
          <input
            type="tel"
            id="phone"
            disabled={isSubmitting}
            aria-invalid={!!errors.phone}
            {...register('phone', {
              required: 'Ange ett giltigt telefonnummer',
              maxLength: 25,
              pattern: {
                value: /^[0-9\s+\-()]*$/,
                message: 'Ange ett giltigt telefonnummer'
              }
            })}
            className={`w-full px-3 py-2 text-sm border ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-500 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            placeholder="Telefonnummer"
          />
          {errors.phone && (
            <p className="mt-1 text-xs text-red-600">{errors.phone.message}</p>
          )}
        </div>
        
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-1">
            E-post <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            disabled={isSubmitting}
            aria-invalid={!!errors.email}
            {...register('email', { 
              required: 'E-post är obligatorisk',
              maxLength: 254,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Ange en giltig e-postadress'
              }
            })}
            className={`w-full px-3 py-2 text-sm border ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-500 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            placeholder="E-post"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>
      </div>
      
      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-semibold text-gray-800 mb-1">
          Meddelande
        </label>
        <textarea
          id="description"
          rows={2}
          disabled={isSubmitting}
          {...register('description', { maxLength: 1000 })}
          className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          placeholder="Kort meddelande om dina behov"
        ></textarea>
      </div>

      {/* Consent */}
      <label className="flex items-start gap-2 text-[12px] text-gray-700 leading-tight">
        <input
          type="checkbox"
          disabled={isSubmitting}
          aria-invalid={!!errors.consent}
          {...register('consent', { required: 'Du måste godkänna villkoren' })}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
        />
        <span>Genom att skicka godkänner du att vi kontaktar dig via telefon eller e-post.</span>
      </label>
      {errors.consent && (
        <p className="text-[11px] text-red-600">{errors.consent.message}</p>
      )}
      
      {/* Error / Success Messages */}
      {submitError && (
        <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm mb-3">
          {submitError}
        </div>
      )}
      {submitSuccess && (
        <div className="p-3 bg-green-100 text-green-700 rounded-md text-sm mb-3">
          Tack för din förfrågan! Vi återkommer så snart som möjligt.
        </div>
      )}
      
      {/* Submit Button */}
      <motion.div
        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        transition={{ duration: 0.2 }}
      >
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full bg-red-600 hover:bg-red-700 text-white text-base sm:text-lg font-['Raleway'] font-extrabold py-2 sm:py-2.5 lg:py-2.5 px-4 sm:px-6 rounded-lg transition-all duration-300 mt-2 hover:shadow-lg ${
            isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? 'Skickar...' : 'Skicka förfrågan'}
        </button>
      </motion.div>
    </form>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Image with Blur */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${heroImage})`,
          filter: 'blur(1px)',
          transform: 'scale(1.02)',
          zIndex: 0,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[hsl(var(--hero-overlay))]/40" />
      </div>
      
      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden" style={{ height: '120px', zIndex: 10 }}>
        <svg 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
          className="w-full h-full block"
        >
          <path 
            d="M0,0V10C0,10,100,100,600,100S1200,10,1200,10v110H0Z" 
            fill="#ffffff"
          />
          <path 
            d="M0,0V30C0,30,100,90,600,90S1200,30,1200,30v90H0Z" 
            fill="#ffffff"
            opacity="0.5"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto lg:pr-[140px] px-4 sm:px-6 text-left pt-16 md:pt-12">
        <div className="relative lg:flex lg:items-center lg:justify-between lg:gap-8">
          {/* Left side - Main content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left lg:pr-0 [@media(min-width:1024px)_and_(max-height:650px)]:scale-[0.95] [@media(min-width:1024px)_and_(max-height:650px)]:origin-top [@media(min-width:1024px)_and_(max-height:650px)]:pl-6 [@media(min-width:1024px)_and_(max-height:650px)]:-mt-5"
          >
            <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-[2.75rem] xl:text-6xl font-light text-white mb-6 leading-[1.1] tracking-tight max-w-3xl antialiased mx-auto lg:mx-0 [@media(min-width:1024px)_and_(max-height:650px)]:text-[3.5rem] [@media(min-width:1024px)_and_(max-height:650px)]:mb-5 [@media(max-width:400px)]:text-3xl [@media(max-width:400px)]:leading-tight">
              Noggrann städning <br /> med skinande resultat
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-lg xl:text-xl text-white/90 mb-8 md:mb-9 lg:mb-8 xl:mb-10 font-light max-w-2xl leading-relaxed antialiased mx-auto lg:mx-0 [@media(min-width:1024px)_and_(max-height:650px)]:text-[1.1rem] [@media(min-width:1024px)_and_(max-height:650px)]:mb-6 [@media(max-width:400px)]:text-sm [@media(max-width:400px)]:leading-tight">
              Professionell städning för hem och företag sedan 1995<br />Miljövänliga produkter
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <motion.a
                href="#tjanster"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="w-full sm:w-auto block"
              >
                <Button 
                  size="lg"
                  className="bg-destructive hover:bg-destructive/90 text-white text-lg px-12 py-7 uppercase font-normal tracking-wide transition-all duration-300 w-full sm:w-auto md:w-auto lg:w-auto xl:w-auto rounded-lg sm:rounded-lg [@media(max-width:640px)]:rounded-lg [@media(max-width:640px)]:px-6 [@media(max-width:640px)]:py-4 [@media(max-width:640px)]:text-base [@media(max-width:640px)]:shadow-md [@media(min-width:1024px)_and_(max-height:650px)]:text-base [@media(min-width:1024px)_and_(max-height:650px)]:px-10 [@media(min-width:1024px)_and_(max-height:650px)]:py-5"
                >
                  Våra Tjänster
                </Button>
              </motion.a>
              
              <motion.a
                href="/galleri"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="w-full sm:w-auto block"
              >
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[hsl(var(--nav-bg))] text-lg px-12 py-7 uppercase font-normal tracking-wide transition-all duration-300 w-full sm:w-auto md:w-auto lg:w-auto xl:w-auto rounded-lg sm:rounded-lg transform -translate-y-[2px] [@media(max-width:640px)]:rounded-none [@media(max-width:640px)]:border-l-0 [@media(max-width:640px)]:border-r-0 [@media(max-width:640px)]:px-6 [@media(max-width:640px)]:py-4 [@media(max-width:640px)]:text-base [@media(max-width:640px)]:shadow-md [@media(max-width:640px)]:translate-y-0 [@media(min-width:1024px)_and_(max-height:650px)]:text-base [@media(min-width:1024px)_and_(max-height:650px)]:px-10 [@media(min-width:1024px)_and_(max-height:650px)]:py-5"
                >
                  Tidigare Projekt
                </Button>
              </motion.a>
            </div>
          </motion.div>

          {/* Right side - Quote Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mt-8 lg:mt-0 w-full max-w-lg lg:w-[430px] [@media(min-width:1024px)_and_(max-height:650px)]:lg:w-[400px] mx-auto lg:mx-0"
          >
            {/* Decorative layer behind the form */}
            <div className="absolute -inset-0.5 bg-gradient-to-br from-red-600 to-red-700 rounded-lg transform rotate-0.5 scale-98 -z-10"></div>
            <div className="absolute -inset-0.5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg transform -rotate-0.5 scale-99 -z-5"></div>
            
            {/* Main form container */}
            <div className="relative bg-white/95 backdrop-blur-sm rounded-lg pt-3 pb-4 px-4 md:px-5 lg:px-6 [@media(min-width:1024px)_and_(max-height:650px)]:pt-2.5 [@media(min-width:1024px)_and_(max-height:650px)]:pb-3.5 [@media(min-width:1024px)_and_(max-height:650px)]:px-4 shadow-2xl border border-white/20">
              {/* Response time badge */}
              <div className="absolute -top-3 -left-2 bg-[#3199da] text-white text-sm font-medium px-4 py-1.5 rounded-full shadow-md">
                Svar inom 24h
              </div>
              <div className="flex justify-center mb-0 [@media(min-width:1024px)_and_(max-height:650px)]:scale-[0.95]" style={{
                marginTop: '-1.75rem',
                transform: 'scale(1.1)',
                transformOrigin: 'center top'
              }}>
                <img 
                  src="/renglans2.webp" 
                  alt="RenGlans Städtjänster" 
                  className="h-24 w-auto transform scale-105 transition-transform duration-200 hover:scale-110"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/renglans2.webp';
                  }}
                />
              </div>
              
              <h2 className="text-xl sm:text-2xl font-extrabold text-gray-800 mt-1 mb-3 sm:mb-4 text-center [@media(min-width:1024px)_and_(max-height:650px)]:text-[1.15rem] [@media(min-width:1024px)_and_(max-height:650px)]:mb-2.5" style={{ fontFeatureSettings: '"liga" 0, "dlig" 0' }}>Få en kostnadsfri offert</h2>
              
              <QuoteForm />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <FloatingActionButtons />
    </section>
  );
};

export default Hero;